// frontend/src/services/endpoints.js
// One place for the API surface, so components don't hand-write URLs.
import api from './api';

const qs = (params = {}) => {
    const clean = Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
    return clean.length ? `?${clean.join('&')}` : '';
};

// ---- search ------------------------------------------------------------
export const search = (q) => api.get(`/search${qs({ q })}`);

// ---- dashboard -------------------------------------------------------
export const getSummary      = (params)  => api.get(`/dashboard/summary${qs(params)}`);
export const getMyDashboard  = ()        => api.get('/dashboard/my');
export const getCharts       = ()        => api.get('/dashboard/charts');
export const getMetrics      = (period, filters = {}) => api.get(`/dashboard/metrics${qs({ period, ...filters })}`);
export const getCalendar     = (params)  => api.get(`/calendar${qs(params)}`);
export const getTeamRollup   = ()        => api.get('/dashboard/team');
export const getMeta         = ()        => api.get('/dashboard/meta');

// ---- parts -----------------------------------------------------------
export const getParts        = (f)       => api.get(`/parts${qs(f)}`);
export const getPart         = (id)      => api.get(`/parts/${id}`);
// Unscoped, minimal fields -- for the global search "workflow status" preview.
export const getPartWorkflow = (id)      => api.get(`/parts/${id}/workflow`);
export const createPart      = (body)    => api.post('/parts', body);
export const updatePart      = (id, b)   => api.put(`/parts/${id}`, b);
export const deletePart      = (id)      => api.delete(`/parts/${id}`);
export const setPartStatus   = (id, b)   => api.put(`/parts/${id}/status`, b);
export const decidePart      = (id, b)   => api.post(`/parts/${id}/decision`, b);
export const getPartComments = (id)      => api.get(`/parts/${id}/comments`);
export const addPartComment  = (id, c)   => api.post(`/parts/${id}/comments`, { comment: c });
export const getPartFiles    = (id)      => api.get(`/parts/${id}/attachments`);
export const getPartHistory  = (id)      => api.get(`/parts/${id}/history`);
export const getPartStats    = ()        => api.get('/parts/stats');

// The api instance defaults to application/json. Setting Content-Type to
// undefined lets axios detect FormData and add the multipart boundary itself;
// hardcoding 'multipart/form-data' would omit the boundary and the upload fails.
const FORM_DATA = { headers: { 'Content-Type': undefined } };

export const uploadPartFiles = (id, files) => {
    const form = new FormData();
    [...files].forEach(f => form.append('files', f));
    return api.post(`/parts/${id}/files`, form, FORM_DATA);
};

export const importParts = (file, onProgress) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/parts/import', form, {
        ...FORM_DATA,
        onUploadProgress: onProgress,
    });
};

// ---- tickets ---------------------------------------------------------
export const getTickets        = (f)     => api.get(`/tickets${qs(f)}`);
export const getTicket         = (id)    => api.get(`/tickets/${id}`);
// Unscoped list for the Add/Edit Part "JIRA Ticket" dropdown -- any ticket,
// not just the current user's own.
export const getTicketOptions  = ()      => api.get('/tickets/lookup');
// Unscoped, minimal fields -- for the global search "workflow status" preview.
export const getTicketWorkflow = (id)    => api.get(`/tickets/${id}/workflow`);
export const createTicket      = (body)  => api.post('/tickets', body);
export const updateTicket      = (id, b) => api.put(`/tickets/${id}`, b);
// Tickets are never hard-deleted -- move status to 'Cancelled' via setTicketStatus instead.
export const setTicketStatus   = (id, b) => api.put(`/tickets/${id}/status`, b);
export const decideTicket      = (id, b) => api.post(`/tickets/${id}/decision`, b);
export const getTicketComments = (id)    => api.get(`/tickets/${id}/comments`);
export const addTicketComment  = (id, c) => api.post(`/tickets/${id}/comments`, { comment: c });
export const getTicketHistory  = (id)    => api.get(`/tickets/${id}/history`);
export const getTicketFiles    = (id)    => api.get(`/tickets/${id}/attachments`);
export const getTicketParts    = (id)    => api.get(`/tickets/${id}/parts`);

export const uploadTicketFiles = (id, files) => {
    const form = new FormData();
    [...files].forEach(f => form.append('files', f));
    return api.post(`/tickets/${id}/files`, form, FORM_DATA);
};

// ---- users -----------------------------------------------------------
export const getUsers = () => api.get('/users');

// ---- profile -----------------------------------------------------------
export const uploadMyPhoto = (file) => {
    const form = new FormData();
    form.append('photo', file);
    return api.post('/auth/me/photo', form, FORM_DATA);
};
export const removeMyPhoto = () => api.delete('/auth/me/photo');

// ---- reports ---------------------------------------------------------
export const listReports = ()          => api.get('/reports');
export const getReport   = (id)        => api.get(`/reports/${id}`);

/**
 * Download a file from the API and hand it to the browser.
 * Goes through axios so the Authorization header is attached.
 */
export async function download(path, filename) {
    const res = await api.get(path, { responseType: 'blob' });
    const blob = new Blob([res.data], {
        type: res.headers['content-type'] || 'application/octet-stream',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Give the browser a tick to start the download before revoking.
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

export const downloadReport = (reportId, format, title) =>
    download(`/reports/export/${reportId}?format=${format}`,
             `${(title || reportId).replace(/[^a-zA-Z0-9]+/g, '_')}.${format === 'excel' ? 'xlsx' : format}`);

export const downloadParts = (format, filters = {}) =>
    download(`/parts/export/${format}${qs(filters)}`,
             `parts_tracker.${format === 'excel' ? 'xlsx' : format}`);

export const downloadPartsTemplate = () =>
    download('/parts/template', 'parts_bulk_upload_template.xlsx');

export const downloadTickets = (filters = {}) =>
    download(`/tickets/export/excel${qs(filters)}`, 'tickets.xlsx');

export const downloadTicketsTemplate = () =>
    download('/tickets/template', 'tickets_bulk_upload_template.xlsx');

export const importTickets = (file, onProgress) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/tickets/import', form, {
        ...FORM_DATA,
        onUploadProgress: onProgress,
    });
};

export { qs };
